import { llm } from '@cotera/api';
import {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputAttachment,
  PromptInputAttachments,
  PromptInputBody,
  PromptInputFooter,
  PromptInputHeader,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  COMPOSER_EDITOR_MAX_HEIGHT,
  type PromptInputMessage,
} from '@cotera/client/v0/components/ai-elements/prompt-input';
import { cn } from '@cotera/client/v0/lib/utils';
import type { Editor } from '@tiptap/react';
import { ChatTipTapInput } from '@cotera/client/v0/components/ai-elements/chat-tiptap-input';
import { trimEmptyEdgeBlocks } from '@cotera/client/app/components/app/rte/lib/trim-empty-blocks';
import { useCocoMentions } from '@cotera/client/app/hooks/use-coco-mentions';
import { useWhoami } from '@cotera/client/app/stores/org';
import { useGatedModelFilter } from '@cotera/client/app/hooks/use-gated-models';
import { ModelPickerMenu } from '@cotera/client/app/components/model-picker-menu';
import {
  removeChatDraft,
  setChatDraft,
} from '@cotera/client/app/v1/chat/models/chat-draft';

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { motion } from 'motion/react';
import { ChevronRight, SkipForward } from 'lucide-react';
import {
  useAction,
  useInScopeModel,
  useWatchableValue,
} from '../actions/framework';
import { ChatModel } from './chat.model';
import { HijackerQueueModel } from './hijacker-queue.model';
import { HijackerWizard } from './input-hijackers/hijacker-wizard';
import { Button } from '@cotera/client/v0/components/ui/button';
import { StopAction } from './actions/stop.action';
import { Watchable } from '../actions/framework';
import type { HijackerQueueState } from './input-hijackers/types';
import {
  useAnalytics,
  useCocoConversationId,
  useChatSurface,
  CocoEvent,
} from '@cotera/client/app/etc';

/** Default/empty hijacker queue state for when HijackerQueueModel is not available (v0 paths) */
const DEFAULT_HIJACKER_QUEUE_STATE: HijackerQueueState = {
  queue: [],
  activeIndex: -1,
  activeItem: null,
  totalPending: 0,
  completedCount: 0,
  canComplete: true,
  setCanComplete: () => {},
  onComplete: () => {},
  onSkip: () => {},
  onPrevious: () => {},
  onDismissAll: () => {},
  hasMultiple: false,
  stepIndicator: { current: 0, total: 0, completed: 0 },
};

/** Static watchable that always returns the default empty state */
const DEFAULT_QUEUE_STATE_WATCHABLE = Watchable.fromValue(
  DEFAULT_HIJACKER_QUEUE_STATE
);

/** Static watchable for null button (no custom button) */
const DEFAULT_BUTTON_WATCHABLE = Watchable.fromValue<React.ReactNode>(null);

/**
 * Group headings for the model dropdown. Only providers whose enum name should
 * not be shown verbatim need an entry; anything unlisted falls back to the raw
 * provider name.
 *
 * `LunaRoute` is the gateway we buy from, not something a customer should see —
 * that offering is branded Cotera Unlimited.
 */
const PROVIDER_GROUP_LABELS: Partial<Record<llm.ModelProvider, string>> = {
  LunaRoute: 'Cotera Unlimited',
};

/**
 * `Object.entries` widens the provider key to `string`, so index the overrides
 * through a string-keyed Map rather than casting the key back.
 */
const PROVIDER_GROUP_LABEL_BY_NAME = new Map(
  Object.entries(PROVIDER_GROUP_LABELS)
);

/** Order of the provider groups (the order they were always listed in). */
const PROVIDER_ORDER: readonly llm.ModelProvider[] = [
  'OpenAI',
  'Anthropic',
  'Google',
  'AzureFoundry',
  'LunaRoute',
];

export const ModelSelector = ({
  model,
  handleModelChange,
}: {
  model: string;
  handleModelChange: (model: string) => void;
}) => {
  const org = useWhoami((s) => s.org);
  const showModel = useGatedModelFilter();
  // Current models grouped by provider; superseded versions go in the
  // "Other models" submenu, grouped by vendor.
  const { groups, otherGroups } = useMemo(() => {
    const toItem = (m: llm.Model) => ({
      id: m.id,
      label: m.name,
      creditMultiplier: m.creditMultiplier,
    });
    const eligible = llm
      .allowedModelsForOrg(
        org.id,
        Object.values(llm.MODELS).filter((m) => m.active)
      )
      .filter((m) => showModel(m.id));
    const { current, legacyByVendor } = llm.groupLegacyModelsByVendor(eligible);
    return {
      groups: PROVIDER_ORDER.map((provider) => ({
        key: provider,
        label: PROVIDER_GROUP_LABEL_BY_NAME.get(provider) ?? provider,
        models: current.filter((m) => m.provider === provider).map(toItem),
      })),
      otherGroups: legacyByVendor.map(({ vendor, models }) => ({
        key: vendor,
        label: vendor,
        models: models.map(toItem),
      })),
    };
  }, [org.id, showModel]);

  // A saved selection the menu no longer offers still needs an entry.
  const currentMeta = llm.getModel(model);
  const currentModelListed = [...groups, ...otherGroups].some((g) =>
    g.models.some((m) => m.id === model)
  );

  return (
    <ModelPickerMenu
      value={model}
      valueLabel={currentMeta?.name ?? model}
      onChange={handleModelChange}
      groups={
        !currentModelListed && currentMeta !== undefined
          ? [
              {
                key: 'current',
                label: 'Current',
                models: [
                  {
                    id: currentMeta.id,
                    label: currentMeta.name,
                    creditMultiplier: currentMeta.creditMultiplier,
                  },
                ],
              },
              ...groups,
            ]
          : groups
      }
      otherGroups={otherGroups}
    />
  );
};

type ChatInputProps = {
  chatModel: ChatModel;
  inputFooter?: ReactNode;
  customSubmitButton?: ReactNode;
  onSubmit?: (message: PromptInputMessage) => void | Promise<void>;
  onStop?: (message?: PromptInputMessage) => void | Promise<void>;
  /**
   * When provided, submitting a message while a run is in flight enqueues it
   * (composer is cleared) instead of cancelling the run. Without this the old
   * behaviour applies: submitting mid-run stops the run and resends. Empty
   * submits still stop the run either way.
   */
  onQueue?: (message: PromptInputMessage) => void;
  /** When true, uses TipTap rich text editor instead of plain textarea */
  useRichText?: boolean;
  /** When true, accepts drops anywhere on document */
  globalDrop?: boolean;
  /** When provided, mention chips in input are clickable */
  onMentionClick?: (
    data: import('@cotera/client/v0/components/ai-elements/readonly-editor-state').MentionClickData
  ) => void;
  /** Placeholder text for the input field */
  placeholder?: string;
};

export const ChatInput = forwardRef<
  {
    setValue: (value: string | ((prev: string) => string)) => void;
    setEditorState: (value: { type: 'doc'; content?: unknown[] }) => void;
    setPlaceholder?: (placeholder: string) => void;
  },
  ChatInputProps
>(
  (
    {
      chatModel,
      inputFooter,
      customSubmitButton,
      onSubmit,
      onStop,
      onQueue,
      useRichText = false,
      globalDrop,
      onMentionClick,
      placeholder = 'What would you like to know?',
    },
    ref
  ) => {
    const tiptapRef = useRef<{
      setValue: (text: string) => void;
      getEditor: () => Editor | null;
      setPlaceholder: (placeholder: string) => void;
    }>(null);
    const inputValue = useWatchableValue(chatModel.inputWatchable);
    const analytics = useAnalytics();
    const cocoConversationId = useCocoConversationId();
    const attachSurface = useChatSurface() ?? 'scratch';
    const {
      options: mentionOptions,
      groups: mentionGroups,
      isLoading: mentionLoading,
    } = useCocoMentions();

    const conversationId = chatModel.id;

    // Persist the in-progress draft (debounced) so it survives a page reload —
    // e.g. the auto-reload that happens when a new app version deploys.
    const draftSaveTimer = useRef<number | null>(null);
    const cancelPendingDraftSave = useCallback(() => {
      if (draftSaveTimer.current !== null) {
        window.clearTimeout(draftSaveTimer.current);
        draftSaveTimer.current = null;
      }
    }, []);
    const saveDraft = useCallback(
      (text: string, editorState?: { type: 'doc'; content?: unknown[] }) => {
        cancelPendingDraftSave();
        draftSaveTimer.current = window.setTimeout(() => {
          setChatDraft(conversationId, { text, editorState });
        }, 400);
      },
      [cancelPendingDraftSave, conversationId]
    );
    useEffect(() => cancelPendingDraftSave, [cancelPendingDraftSave]);

    /**
     * Drop the persisted draft and empty the composer immediately on send.
     * TipTap is uncontrolled, so clearing `inputWatchable` alone leaves the
     * visible text in place. We also remove the sessionStorage draft up front:
     * agent/workflow submits await preSubmit work, and when the URL seed clears
     * afterwards `useRestoreChatDraft` can otherwise resurrect the just-sent
     * text into the box.
     */
    const clearComposerAndDraft = useCallback(() => {
      cancelPendingDraftSave();
      removeChatDraft(conversationId);
      chatModel.inputWatchable.set('');
      tiptapRef.current?.setValue('');
    }, [cancelPendingDraftSave, chatModel.inputWatchable, conversationId]);

    const status = chatModel.status;
    /** Assistant turn still running; not inferred from incomplete tools alone. */
    const isRunInFlight = status === 'streaming' || status === 'submitted';
    const stopAction = useAction(StopAction);

    useImperativeHandle(ref, () => ({
      setValue: (value: string | ((prev: string) => string)) => {
        const text =
          typeof value === 'function'
            ? value(chatModel.inputWatchable.snapshot())
            : value;
        chatModel.inputWatchable.set(text);
        tiptapRef.current?.setValue(text);
      },
      setEditorState: (value: { type: 'doc'; content?: unknown[] }) => {
        const editor = tiptapRef.current?.getEditor();
        if (editor !== null && editor !== undefined) {
          editor.commands.setContent(
            value as Parameters<typeof editor.commands.setContent>[0]
          );
          chatModel.inputWatchable.set(editor.getText());
        }
      },
      setPlaceholder: (placeholder: string) => {
        tiptapRef.current?.setPlaceholder(placeholder);
      },
    }));

    const handleStopGeneration = useCallback(
      async (message?: PromptInputMessage) => {
        if (onStop !== undefined) {
          await onStop(message);
        } else {
          await stopAction.execute(
            message?.type === 'editor-state'
              ? {
                  editorState: message.editorState,
                  files: message.files,
                }
              : {
                  text: message?.text,
                  files: message?.files,
                }
          );
        }
      },
      [onStop, stopAction]
    );

    const handleSubmitButtonClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        if (!isRunInFlight) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        void handleStopGeneration();
      },
      [handleStopGeneration, isRunInFlight]
    );

    const handleSubmit = useCallback(
      async (message: PromptInputMessage) => {
        const hasContent =
          (message.type === 'text'
            ? message.text.trim().length > 0
            : (message.editorState.content?.length ?? 0) > 0) ||
          (message.files?.length ?? 0) > 0;

        if (isRunInFlight) {
          // Queue the message instead of cancelling the run, when the surface
          // supports queueing and there is something to send. An empty submit
          // mid-run still means "stop" and falls through below.
          if (onQueue !== undefined && hasContent) {
            clearComposerAndDraft();
            onQueue(message);
            return;
          }
          await handleStopGeneration(message);
          return;
        }

        if (!hasContent) {
          return;
        }

        // Capture enough to restore on failure. Rich-text sends carry editor
        // state on the message; plain text falls back to the watchable snapshot.
        const previousInput = chatModel.inputWatchable.snapshot();
        const previousEditorState =
          message.type === 'editor-state' ? message.editorState : undefined;
        clearComposerAndDraft();

        try {
          if (onSubmit !== undefined) {
            // The consumer's onSubmit can run async work before the message is
            // actually sent (e.g. agent/workflow preSubmit). Composer is already
            // cleared above so the text does not linger during that gap.
            await onSubmit(message);
            await chatModel.sendMessage(message);
            return;
          }
          await chatModel.sendMessage(message);
        } catch (error) {
          // SubmitCocoChatAction also restores via setInputEditorState; this
          // covers v0 / plain onSubmit paths that don't.
          if (previousEditorState !== undefined) {
            const editor = tiptapRef.current?.getEditor();
            if (editor !== null && editor !== undefined) {
              editor.commands.setContent(
                previousEditorState as Parameters<
                  typeof editor.commands.setContent
                >[0]
              );
            }
            chatModel.inputWatchable.set(previousInput);
          } else {
            chatModel.inputWatchable.set(previousInput);
            tiptapRef.current?.setValue(previousInput);
          }
          throw error;
        }
      },
      [
        chatModel,
        clearComposerAndDraft,
        handleStopGeneration,
        isRunInFlight,
        onQueue,
        onSubmit,
      ]
    );

    const footer = inputFooter ?? (
      <PromptInputTools>
        <PromptInputActionMenu>
          <PromptInputActionMenuTrigger />
          <PromptInputActionMenuContent>
            <PromptInputActionAddAttachments />
          </PromptInputActionMenuContent>
        </PromptInputActionMenu>
      </PromptInputTools>
    );

    const getMessageContent = useCallback(
      (files: import('ai').FileUIPart[]) => {
        if (useRichText) {
          const editor = tiptapRef.current?.getEditor();
          if (editor !== undefined && editor !== null) {
            const json = editor.getJSON();
            const text = editor.getText().trim();
            if (text === '' && files.length === 0) {
              return {
                message: { type: 'text' as const, text: '', files },
                clear: () => {},
              };
            }
            return {
              message: {
                type: 'editor-state' as const,
                editorState: trimEmptyEdgeBlocks(json) as {
                  type: 'doc';
                  content?: unknown[];
                },
                files,
              },
              clear: () => {
                editor.commands.clearContent();
                chatModel.inputWatchable.set('');
              },
            };
          }
        }
        return {
          message: { type: 'text' as const, text: inputValue, files },
        };
      },
      [useRichText, inputValue, chatModel.inputWatchable]
    );

    // Subscribe to hijacker queue model for wizard UI
    // Falls back to default watchables in v0 paths where HijackerQueueModel is not provided
    const hijackerQueueModel = useInScopeModel(HijackerQueueModel);
    const queueState = useWatchableValue(
      hijackerQueueModel?.queueStateWatchable ?? DEFAULT_QUEUE_STATE_WATCHABLE
    );
    const modelCustomSkipButton = useWatchableValue(
      hijackerQueueModel?.customSkipButton ?? DEFAULT_BUTTON_WATCHABLE
    );
    const modelCustomSubmitButton = useWatchableValue(
      hijackerQueueModel?.customSubmitButton ?? DEFAULT_BUTTON_WATCHABLE
    );
    // Memoize register callbacks to prevent infinite loops
    const registerSkipButton = useCallback(
      (btn: React.ReactNode | null) => {
        hijackerQueueModel?.registerSkipButton(btn);
      },
      [hijackerQueueModel]
    );

    const registerSubmitButton = useCallback(
      (btn: React.ReactNode | null) => {
        hijackerQueueModel?.registerSubmitButton(btn);
      },
      [hijackerQueueModel]
    );

    // Build hijack content and footer actions from model state
    const hijackContent = useMemo(() => {
      if (queueState?.activeItem === null || queueState === undefined) {
        return null;
      }

      return (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="mb-3 flex w-full min-h-0 max-h-[min(42vh,26rem)] flex-col overflow-hidden px-2"
        >
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <HijackerWizard
              activeItem={queueState.activeItem}
              activeIndex={queueState.activeIndex}
              totalItems={queueState.queue.length}
              stepIndicator={queueState.stepIndicator}
              canComplete={queueState.canComplete}
              setCanComplete={queueState.setCanComplete}
              onComplete={queueState.onComplete}
              onSkip={queueState.onSkip}
              onPrevious={queueState.onPrevious}
              onDismissAll={queueState.onDismissAll}
              registerSkipButton={registerSkipButton}
              registerSubmitButton={registerSubmitButton}
              chatModel={chatModel}
              readonly={false}
            />
          </div>
        </motion.div>
      );
    }, [queueState, registerSkipButton, registerSubmitButton, chatModel]);

    // Build footer actions for wizard
    const hijackFooterActions = useMemo(() => {
      if (queueState?.activeItem === null || queueState === undefined) {
        return undefined;
      }

      const defaultContinueButton = (
        <Button
          type="button"
          size="sm"
          onClick={queueState.onComplete}
          disabled={!queueState.canComplete}
          className="h-8 text-sm gap-1.5"
        >
          {queueState.activeIndex === queueState.queue.length - 1
            ? 'Finish'
            : 'Continue'}
          <ChevronRight className="h-4 w-4" />
        </Button>
      );

      const defaultSkipButton = (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={queueState.onSkip}
          className="h-8 text-sm text-muted-foreground hover:text-foreground gap-1.5"
        >
          <SkipForward className="h-4 w-4" />
          Skip
        </Button>
      );

      return (
        <div className="flex items-center gap-2">
          {modelCustomSkipButton ?? defaultSkipButton}
          {modelCustomSubmitButton ?? defaultContinueButton}
        </div>
      );
    }, [queueState, modelCustomSkipButton, modelCustomSubmitButton]);

    // Show wizard footer actions when input is empty and wizard is active
    const showWizardActions =
      hijackFooterActions !== undefined && !inputValue.trim();

    return (
      <PromptInput
        globalDrop={globalDrop}
        onSubmit={handleSubmit}
        onFilesAdded={(count) =>
          analytics.track(CocoEvent.ChatFileAttached, {
            surface: attachSurface,
            fileCount: count,
            source: 'chat',
            ...(cocoConversationId !== null
              ? { conversationId: cocoConversationId }
              : {}),
          })
        }
        getMessageContent={useRichText ? getMessageContent : undefined}
        className="divide-y-0 bg-background max-w-5xl mx-auto"
      >
        {hijackContent}
        <PromptInputHeader>
          <PromptInputAttachments>
            {(attachment) => <PromptInputAttachment data={attachment} />}
          </PromptInputAttachments>
        </PromptInputHeader>
        <PromptInputBody>
          {useRichText ? (
            <ChatTipTapInput
              ref={tiptapRef}
              placeholder={placeholder}
              className={cn(
                COMPOSER_EDITOR_MAX_HEIGHT,
                'overflow-y-auto px-5 md:text-base'
              )}
              onTextChange={(text) => chatModel.inputWatchable.set(text)}
              onChange={(editor) =>
                saveDraft(
                  editor.getText(),
                  editor.getJSON() as { type: 'doc'; content?: unknown[] }
                )
              }
              mentionOptions={mentionOptions}
              mentionGroups={mentionGroups}
              mentionLoading={mentionLoading}
              onMentionClick={onMentionClick}
            />
          ) : (
            <PromptInputTextarea
              className="px-5 md:text-base"
              placeholder={placeholder}
              onChange={(e) => {
                chatModel.inputWatchable.set(e.target.value);
                saveDraft(e.target.value);
              }}
              value={inputValue}
            />
          )}
        </PromptInputBody>
        <PromptInputFooter className="px-3.5 py-2.5">
          {footer}
          <div className="flex items-center gap-1">
            <div className="flex items-center gap-1 mr-0.5">
              {showWizardActions
                ? hijackFooterActions
                : isRunInFlight
                ? customSubmitButton ?? (
                    <PromptInputSubmit
                      aria-label="Stop generating"
                      size="icon-sm"
                      status={status}
                      onClick={handleSubmitButtonClick}
                    />
                  )
                : customSubmitButton ?? (
                    <PromptInputSubmit
                      aria-label="Send message"
                      disabled={!inputValue}
                      size="icon-sm"
                      status={status}
                    />
                  )}
            </div>
          </div>
        </PromptInputFooter>
      </PromptInput>
    );
  }
);

ChatInput.displayName = 'ChatInput';
